package com.evoapps.evoshift.domain.calendar

import com.evoapps.evoshift.domain.model.Shift
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/**
 * A person's shifts as an iCalendar file (RFC 5545) for "Add to calendar" (SH-45). The same event
 * identity as the roster e-mails (functions/calendar.js): UID `shift-<id>@evoshift.app` and
 * SEQUENCE from the shift's last change, so importing the app's file and the e-mail's updates one
 * event instead of adding two.
 *
 * Times: shift times are the workplace's local wall clock. In Europe/Warsaw (the market) they are
 * written as they are with `TZID=Europe/Warsaw` and its VTIMEZONE, which needs no time-zone
 * database (the web build has none); other zones are converted to UTC when the platform knows the
 * zone, else written as floating local times.
 */
object ShiftCalendar {
    const val WARSAW = "Europe/Warsaw"
    private const val UID_DOMAIN = "evoshift.app"
    private val SEQUENCE_EPOCH = Instant.parse("2024-01-01T00:00:00Z")

    data class Event(
        val uid: String,
        val start: LocalDateTime,
        val end: LocalDateTime,
        val summary: String,
        val location: String,
        val sequence: Long
    )

    /** "EVoShift: Barista @ Aurora", or "EVoShift: Aurora" without a role. */
    fun summary(role: String?, workplace: String): String {
        val where = workplace.ifBlank { "EVoShift" }
        return if (role.isNullOrBlank()) "EVoShift: $where" else "EVoShift: $role @ $where"
    }

    /** Seconds from 2024-01-01 to [updatedAt] (0 when unknown), as functions/calendar.js sequenceOf. */
    fun sequenceOf(updatedAt: String?): Long {
        val at = updatedAt?.let { runCatching { Instant.parse(it) }.getOrNull() } ?: return 0
        return (at - SEQUENCE_EPOCH).inWholeSeconds.coerceAtLeast(0)
    }

    /** The event of [shift], or null when its planned times are unreadable or not in order. */
    fun event(shift: Shift, summary: String, location: String): Event? {
        val start = localDateTime(shift.plannedTime.start) ?: return null
        val end = localDateTime(shift.plannedTime.end) ?: return null
        if (end <= start) return null
        return Event(
            uid = "shift-${shift.id}@$UID_DOMAIN",
            start = start,
            end = end,
            summary = summary,
            location = location,
            sequence = sequenceOf(shift.metadata.updatedAt ?: shift.metadata.createdAt)
        )
    }

    /** The calendar file: CRLF line ends, folded at 75 octets. [now] is the DTSTAMP. */
    fun build(events: List<Event>, timeZone: String, name: String, now: Instant): String {
        val zone = timeZone.ifBlank { WARSAW }
        val utcZone = if (zone == WARSAW) null else runCatching { TimeZone.of(zone) }.getOrNull()
        val lines = mutableListOf(
            "BEGIN:VCALENDAR",
            "VERSION:2.0",
            "PRODID:-//EVoApps//EVoShift//EN",
            "CALSCALE:GREGORIAN",
            "METHOD:PUBLISH"
        )
        if (name.isNotBlank()) lines += "X-WR-CALNAME:${text(name)}"
        if (zone == WARSAW) lines += WARSAW_VTIMEZONE
        fun time(property: String, at: LocalDateTime): String = when {
            zone == WARSAW -> "$property;TZID=$WARSAW:${local(at)}"
            utcZone != null -> "$property:${utc(at.toInstant(utcZone))}"
            else -> "$property:${local(at)}"
        }
        for (event in events.sortedBy { it.start }) {
            lines += "BEGIN:VEVENT"
            lines += "UID:${event.uid}"
            lines += "DTSTAMP:${utc(now)}"
            lines += time("DTSTART", event.start)
            lines += time("DTEND", event.end)
            lines += "SUMMARY:${text(event.summary)}"
            if (event.location.isNotBlank()) lines += "LOCATION:${text(event.location)}"
            lines += "SEQUENCE:${event.sequence}"
            lines += "STATUS:CONFIRMED"
            lines += "TRANSP:OPAQUE"
            lines += "END:VEVENT"
        }
        lines += "END:VCALENDAR"
        return lines.joinToString("\r\n", postfix = "\r\n") { fold(it) }
    }

    /** An iCalendar TEXT value: backslash, semicolon, comma and line breaks escaped. */
    fun text(value: String): String = value
        .replace("\\", "\\\\").replace(";", "\\;").replace(",", "\\,")
        .replace("\r\n", "\\n").replace("\n", "\\n")

    /** Folds [line] at 75 octets of UTF-8, never inside a character; continuation lines start with a space. */
    fun fold(line: String): String {
        val out = StringBuilder()
        var bytes = 0
        var first = true
        var i = 0
        while (i < line.length) {
            val c = line[i]
            val pair = c.isHighSurrogate() && i + 1 < line.length
            val size = when {
                pair -> 4
                c.code < 0x80 -> 1
                c.code < 0x800 -> 2
                else -> 3
            }
            val limit = if (first) 75 else 74
            if (bytes + size > limit) {
                out.append("\r\n ")
                bytes = 0
                first = false
            }
            out.append(c)
            if (pair) out.append(line[i + 1])
            bytes += size
            i += if (pair) 2 else 1
        }
        return out.toString()
    }

    private fun localDateTime(iso: String): LocalDateTime? =
        runCatching { LocalDateTime.parse(iso.take(19)) }.getOrNull()

    private fun two(n: Int) = n.toString().padStart(2, '0')

    private fun local(at: LocalDateTime) =
        "${at.year}${two(at.month.ordinal + 1)}${two(at.day)}T${two(at.hour)}${two(at.minute)}${two(at.second)}"

    private fun utc(at: Instant) = local(at.toLocalDateTime(TimeZone.UTC)) + "Z"

    /** Europe/Warsaw since 1996: CET, CEST from the last Sunday of March to the last of October. */
    private val WARSAW_VTIMEZONE = listOf(
        "BEGIN:VTIMEZONE",
        "TZID:$WARSAW",
        "X-LIC-LOCATION:$WARSAW",
        "BEGIN:DAYLIGHT",
        "TZOFFSETFROM:+0100",
        "TZOFFSETTO:+0200",
        "TZNAME:CEST",
        "DTSTART:19700329T020000",
        "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU",
        "END:DAYLIGHT",
        "BEGIN:STANDARD",
        "TZOFFSETFROM:+0200",
        "TZOFFSETTO:+0100",
        "TZNAME:CET",
        "DTSTART:19701025T030000",
        "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU",
        "END:STANDARD",
        "END:VTIMEZONE"
    )
}
